/**
 * Per-album-key circuit breaker for local album open → getAlbum failures.
 * Prevents render/fetch loops when IPC returns errors or stale ghost tiles.
 */

const MAX_FAILURES = 3;
const WINDOW_MS = 30_000;

export const ALBUM_OPEN_CIRCUIT_BREAKER_MESSAGE =
  "This album couldn't be opened — refresh your library or try again later";

type BreakerState = {
  failures: number[];
  open: boolean;
};

const breakers = new Map<string, BreakerState>();

function pruneOldFailures(failures: number[], now: number): number[] {
  return failures.filter((t) => now - t < WINDOW_MS);
}

export function resetAlbumOpenCircuit(albumKey: string): void {
  const key = String(albumKey || '').trim();
  if (!key) return;
  breakers.delete(key);
}

export function isAlbumOpenCircuitOpen(albumKey: string): boolean {
  const key = String(albumKey || '').trim();
  if (!key) return false;
  const state = breakers.get(key);
  if (!state) return false;
  if (!state.open) return false;
  const now = Date.now();
  const recent = pruneOldFailures(state.failures, now);
  if (recent.length < MAX_FAILURES) {
    state.failures = recent;
    state.open = false;
    return false;
  }
  return true;
}

export type AlbumOpenFailureResult = {
  attempt: number;
  breakerOpen: boolean;
  shouldFetch: boolean;
};

/** Record a failed open fetch; returns whether another fetch may run. */
export function recordAlbumOpenFailure(albumKey: string): AlbumOpenFailureResult {
  const key = String(albumKey || '').trim();
  if (!key) {
    return { attempt: 0, breakerOpen: true, shouldFetch: false };
  }
  const now = Date.now();
  let state = breakers.get(key);
  if (!state) {
    state = { failures: [], open: false };
    breakers.set(key, state);
  }
  state.failures = pruneOldFailures(state.failures, now);
  state.failures.push(now);
  const attempt = state.failures.length;
  if (attempt >= MAX_FAILURES) {
    state.open = true;
    return { attempt, breakerOpen: true, shouldFetch: false };
  }
  return { attempt, breakerOpen: false, shouldFetch: true };
}

/** Successful fetch clears the breaker for this album key. */
export function recordAlbumOpenSuccess(albumKey: string): void {
  resetAlbumOpenCircuit(albumKey);
}
